import React, { useMemo, useState } from 'react'
import { DocumentNode, useQuery } from '@apollo/client'
import { Hint, Input, Loading, Popover, Tag } from '@tecsinapse/cortex-react'
import { FiX } from 'react-icons/fi'
import { OptionSelect } from '@/core/utils/types'
import { useDebounce } from 'use-debounce'
import { FieldValues, useController, useFormContext } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'

interface FormAutocompleteProps<T extends FieldValues> {
  name: string
  placeholder?: string
  label?: string
  onSelect?: (value: string) => void
  popoverClassName?: string
  query: DocumentNode
  dataPath?: string
  options?: OptionSelect[]
  useQueryAutocomplete?: boolean
  optionSelectFromT?: (value: T) => OptionSelect
  disabled?: boolean
  allowNewOptions?: boolean
}

export const FormAutocomplete = <T extends FieldValues>({
  name,
  placeholder,
  label,
  onSelect,
  popoverClassName,
  query,
  dataPath,
  disabled = false,
  options = [],
  useQueryAutocomplete = false,
  allowNewOptions = false,
  optionSelectFromT
}: FormAutocompleteProps<T>) => {
  const { setValue, control } = useFormContext()
  const {
    field: { value = [] },
    fieldState: { error: formError },
    formState: { errors, submitCount }
  } = useController({ name, control })

  const [inputValue, setInputValue] = useState<string>('')
  const [debouncedValue] = useDebounce(inputValue, 1000)
  const [open, setOpen] = useState(false)

  const { data, loading, error } = useQuery<Record<string, T[]>>(query, {
    variables: { nome: debouncedValue },
    skip: !useQueryAutocomplete || !debouncedValue.trim()
  })

  const filteredOptions = useMemo<OptionSelect[]>(() => {
    if (error) return []

    let optionsList: OptionSelect[]

    if (useQueryAutocomplete) {
      optionsList = (data?.[dataPath ?? ''] ?? [])
        .map(item => optionSelectFromT?.(item))
        .filter(it => it !== undefined)
    } else {
      optionsList = (options ?? []).filter(option =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    }

    if (
      inputValue.trim() &&
      !optionsList.some(
        option => option.label?.toLowerCase() === inputValue.toLowerCase()
      ) &&
      allowNewOptions
    ) {
      return [...optionsList, { label: inputValue, value: inputValue }]
    }

    return optionsList
  }, [
    data,
    dataPath,
    error,
    inputValue,
    optionSelectFromT,
    options,
    useQueryAutocomplete,
    allowNewOptions
  ])

  const handleSelect = (option: OptionSelect) => {
    const currentValues = value ?? []

    if (
      !currentValues.some(
        (selected: OptionSelect) => selected.value === option.value
      )
    ) {
      setValue(name, [...currentValues, option], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      })
    }

    setInputValue('')
    setOpen(false)
    onSelect?.(option.value)
  }

  const handleRemoveOption = (id: string) => {
    setValue(
      name,
      (value ?? []).filter((option: OptionSelect) => option.value !== id, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      })
    )
  }

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    setInputValue(value)
    if (value.length > 0) {
      setOpen(true)
    } else {
      setOpen(false)
    }
  }

  return (
    <div className='relative h-full w-full'>
      <Popover.Root
        placement='bottom-start'
        controlled
        isOpen={open}
        setIsOpen={setOpen}
      >
        <Popover.Trigger>
          <>
            <Input.Root
              label={label}
              value={inputValue}
              onChange={onChange}
              placeholder={placeholder}
              disabled={disabled}
            />
            {submitCount > 0 && formError ? (
              <ErrorMessage
                errors={errors}
                name={name}
                render={({ message }) => (
                  <Hint variants={{ intent: 'error' }}>{message}</Hint>
                )}
              />
            ) : null}
          </>
        </Popover.Trigger>

        <Popover.Content
          className={`w-full bg-white shadow-md rounded-md ${popoverClassName || ''}`}
          initialFocus={-1}
        >
          <div className='p-mili'>
            {loading ? (
              <div className='flex justify-center p-centi'>
                <Loading className='w-8 h-8' />
              </div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  className='flex w-full items-center gap-centi p-centi cursor-pointer hover:bg-gray-100'
                  onClick={() => handleSelect(option)}
                >
                  <span className='text-base truncate'>{option.label}</span>
                </div>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Root>

      <div className='flex flex-wrap gap-centi mt-centi'>
        {(value || []).map((option: OptionSelect) => (
          <Tag.Face key={option.value} className='bg-primary-medium text-white'>
            <Tag.Label className='text-white'>{option.label}</Tag.Label>
            <FiX
              size={16}
              className='text-white cursor-pointer mt-[3px]'
              onClick={() => {
                if (!disabled) handleRemoveOption(option.value)
              }}
            />
          </Tag.Face>
        ))}
      </div>
    </div>
  )
}
